/* eslint-disable global-require */
import axios from "axios";
import cms from "../../../services/cms/cms";
import logOut from "../../../services/utilities/log-out";
import { csContext } from "../../../services/cs-context";
import {
  CmsDocumentTypes,
  CmsResourceRelationship,
} from "../../../services/cms/cms-type-definitions";
import cmsContentMap from "../../../services/cms/cms-content-map";
import { GroupDescriptionApiModel, TeamApiModel, OrganisationDescriptionApiModel } from "../../../api-models";
import { goToPrismic } from "../../../router/router";
import { SideNavOptions, SideNavSections, NavItem } from "./side-nav.types";

export function buildSideNavSections(options: SideNavOptions): SideNavSections {
  const sideNavSections: SideNavSections = {
    id: "home",
    name: "Home",
    imageUrl: require("../../../assets/brand/gyre-icon-sm-rounded.png"),
    link: "/home/",
    items: [
      {
        name: "My Teams",
        navClosedImage: require("../../../assets/side-nav/Teams-icon-reversed.gif"),
        navOpenImage: require("../../../assets/side-nav/Teams-icon-forward.gif"),
        activePaths: [
          "/teamreport",
          "/team/dashboard",
          "/teamviewpointsreport",
          "/team/effectiveness",
          "/teameffectivenessreport",
          "/team/create",
          "/team/members",
          "/team/invite",
          "/teamgoals",
        ],
        activePriority: 2,
        divider: false,
        items: options.canCreateTeams
          ? [
              {
                name: "Setup a team",
                imageUrl: require("../../../assets/create-team.png"),
                link: "/team/create/",
              },
            ]
          : [],
      },
      {
        id: csContext.user.id,
        name: "My Dashboard",
        navClosedImage: require("../../../assets/side-nav/Journey-icon-reversed.gif"),
        navOpenImage: require("../../../assets/side-nav/Journey-icon-forward.gif"),
        activePaths: [
          "/soloreport",
          "/viewpoints",
          "/home?view=MyDashboard",
          "/resources/wheel/personal-journey-wheel/",
          "/session/",
          "/develop",
          "/resources/develop/",
        ],
        activePriority: 3,
        divider: true,
        items: [
          {
            name: "My Dashboard",
            link: "/home?view=MyDashboard",
            imageUrl: require("../../../assets/side-nav/Journey-icon-reversed.gif"),
          },
          {
            name: "My Pathway",
            link: options.currentPersonalPathwayUid
              ? `/my/pathway/${options.currentPersonalPathwayUid}`
              : "/my/outcomes/select",
            imageUrl: require("../../../assets/side-nav/target-gw.png"),
          },
          {
            name: "My Solo",
            link: options.hasSolo ? "/soloreport/" : "/session/",
            // head: "solo",
            imageUrl: require("../../../assets/side-nav/solo-head-icon-gw-2.png"),
          },
          {
            name: "My Viewpoints",
            link: options.hasViewpoints ? "/viewpoints/" : "/my/pathway/viewpoints-invite",
            // head: "viewpoints",
            imageUrl: require("../../../assets/side-nav/vp-head-icon-gw-2.png"),
          },
          {
            name: "Develop",
            link: options.hasDevelop ? "/develop/" : "/resources/playlists/the-34-strengths",
            imageUrl: require("../../../assets/side-nav/develop.png"),
          },
        ],
      },
      {
        name: "Resources",
        navClosedImage: require("../../../assets/side-nav/Resources-icon-reversed.gif"),
        navOpenImage: require("../../../assets/side-nav/Resources-icon-forward.gif"),
        activePaths: ["/resources"],
        activePriority: 6,
        divider: false,
        items: [],
      },
      {
        name: "Settings",
        navClosedImage: require("../../../assets/side-nav/Settings-icon-reversed.gif"),
        navOpenImage: require("../../../assets/side-nav/Settings-icon-forward.gif"),
        activePaths: ["/manage", "/myaccount", "/myaccount/changepassword"],
        activePriority: 1,
        divider: false,
        items: [
          {
            name: "My account",
            link: "/myaccount/",
            imageUrl: require("../../../assets/side-nav/my-account.png"),
          },
          {
            name: "Log off",
            link: "logout",
            imageUrl: require("../../../assets/side-nav/log-out.png"),
          },
        ],
      },
    ],
  };

  if (options.hasAccreditedFunctionality) {
    // Insert My Organisation section between My Dashboard and Resources
    // Find the index of Resources section (should be index 2: My Teams, My Dashboard, Resources, Settings)
    const resourcesIndex = sideNavSections.items.findIndex(item => item.name === "Resources");
    sideNavSections.items.splice(resourcesIndex, 0, {
      name: "My Organisations",
      navClosedImage: require("../../../assets/side-nav/Organisation-icon-reversed.svg"),
      navOpenImage: require("../../../assets/side-nav/Organisation-icon-forward.svg"),
      activePaths: ["/myorganisation"],
      activePriority: 4,
      divider: false,
      items: [], // Will be populated asynchronously with actual organisations
    });
  }

  if (options.hasWatchGroups) {
    sideNavSections.items.splice(-2, 0, {
      name: "Watch Groups",
      navClosedImage: require("../../../assets/side-nav/Watch-group-icon-reversed.gif"),
      navOpenImage: require("../../../assets/side-nav/Watch-group-icon-forward.gif"),
      activePaths: ["/team/watch"],
      activePriority: 1,
      divider: true,
      items: [],
    });
  }

  if (options.isAdmin) {
    sideNavSections.items
      .find((item) => item.name === "Settings")!
      .items!.unshift(
        {
          name: "ADMIN",
          link: "/admin/",
        },
        {
          name: "GO TO PRISMIC",
          link: "prismic",
        },
      );
  }

  return sideNavSections;
}

export function navigateTo(route: string | CmsResourceRelationship): void {
  if (typeof route === "object") {
    window.location.href = cms.linkResolver(route);
  } else if (route === "logout") {
    logOut();
  } else if (route === "prismic") {
    goToPrismic();
  } else {
    window.location.href = route;
  }
}

export function isSectionActive(sectionPaths: string[]): boolean {
  const currentPath = window.location.pathname;
  for (let i = 0; i < sectionPaths.length; i++) {
    const path = sectionPaths[i];

    if (currentPath.startsWith("/session") && path.startsWith("/session")) {
      if (currentPath === "/session/" && path === "/session/") {
        return true;
      }
      if (
        currentPath.startsWith("/session/workshop/team-insights-pathway") &&
        path.startsWith("/session/workshop/team-insights-pathway")
      ) {
        return true;
      }
    } else if (currentPath.startsWith(path)) {
      return true;
    }
  }

  return false;
}

export async function getTeamNavItems(): Promise<NavItem[]> {
  const { data }: { data: TeamApiModel[] } = await axios.get("/api/teams");

  return data.map((team) => {
    if (!team.id) return undefined;

    return {
      ...team,
      link: `/home?team=${team.id}&view=TeamDashboard`,
    };
  }) as NavItem[];
}

export function getWatchGroupNavItems(watchGroups: GroupDescriptionApiModel[]): NavItem[] {
  return watchGroups.map((group) => {
    return {
      id: `watch-group-${group.id}`,
      name: group.name,
      link: `/team/watch/${group.id}`,
    };
  }) as NavItem[];
}

// Cache for organisation data to avoid duplicate API calls
let cachedOrganisationData: OrganisationDescriptionApiModel[] | null = null;

export async function checkAccreditedFunctionality(): Promise<{ hasAccess: boolean; organisations: OrganisationDescriptionApiModel[] }> {
  try {
    const { data }: { data: OrganisationDescriptionApiModel[] } = await axios.get("/api/accrediteduserarea/organisation");
    cachedOrganisationData = data; // Cache the data
    const hasAccess = Array.isArray(data) && data.length > 0;
    return {
      hasAccess,
      organisations: data
    };
  } catch (error) {
    // If the API call fails (e.g., user doesn't have permission), return false
    cachedOrganisationData = [];
    return {
      hasAccess: false,
      organisations: []
    };
  }
}

export function getUserState(): SideNavOptions {
  const { userProfile, userDocument } = csContext;
  return !!userProfile || !!userDocument
    ? {
        userId: userProfile.user.id,
        isAdmin: userProfile.isAdmin,
        currentPersonalPathwayUid: userProfile.currentPersonalPathwayUid,
        hasSolo: userProfile.soloReportLockStatus === "Unlocked",
        hasViewpoints: userProfile.viewpointsReportLockStatus === "Unlocked",
        hasDevelop: !!userProfile.developProgress,
        canCreateTeams: userDocument.platformPermissions.includes("CreateTeams"),
        hasWatchGroups: userDocument.groupsWatched.length > 0,
        hasAccreditedFunctionality: false, // Will be set asynchronously
      }
    : {
        userId: "",
        isAdmin: false,
        hasSolo: false,
        hasViewpoints: false,
        hasDevelop: false,
        canCreateTeams: false,
        hasWatchGroups: false,
        hasAccreditedFunctionality: false,
        currentPersonalPathwayUid: null,
      };
}

export async function getResourcesNavItems(): Promise<NavItem[]> {
  const resources = await cms.getByUid(
    CmsDocumentTypes.CmsResourceThemesPage,
    cmsContentMap.homePage.resourcesThemesPageUid,
  );

  return resources.data.body.map((slice) => {
    const carousel = slice.primary.tile_carousel_embed_link;
    const { id, data } = carousel;

    return {
      id,
      imageUrl:
        data?.side_nav_icon?.url ?? require("../../../assets/brand/gyre-logo-full-rounded.png"),
      name: data.title[0].text,
      items: data.items.map(({ link }) => ({
        id: link.id,
        name: link.data?.title?.[0].text,
        imageUrl:
          link.data?.title_image?.tile.url ||
          require("../../../assets/brand/gyre-logo-full-rounded.png"),
        link,
      })),
    };
  }) as NavItem[];
}

export async function getOrganisationNavItems(): Promise<NavItem[]> {
  // Use cached data if available, otherwise make API call
  let organisations: OrganisationDescriptionApiModel[];
  
  if (cachedOrganisationData !== null) {
    organisations = cachedOrganisationData;
  } else {
    try {
      const { data }: { data: OrganisationDescriptionApiModel[] } = await axios.get("/api/accrediteduserarea/organisation");
      organisations = data;
      cachedOrganisationData = data; // Cache for future use
    } catch (error) {
      organisations = [];
      cachedOrganisationData = [];
    }
  }
  
  return organisations.map((org) => ({
    id: org.id,
    name: org.name,
    link: `/myorganisation/details/${org.id}`,
    imageUrl: require("../../../assets/side-nav/Teams-icon-reversed.gif"),
  }));
}
